import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { money } from 'sectors/pilates/members/components/paymentReceive/ui';
import { payMethodLabel } from '../../modal/MemberDetailModal/utils/memberDetailUtils';

export function PaymentDeleteConfirmModal({
  open,
  payment,
  memberLabel = 'Üye',
  onClose,
  onConfirm, // async (paymentId) => void
}) {
  const theme = useMuiTheme();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const paymentId = payment?.id;

  const amountTry = useMemo(() => Number(payment?.amountTry || 0), [payment]);
  const paidDate = payment?.paidDate || '—';
  const method = payMethodLabel(payment?.method);

  const canDelete = !!paymentId && !saving;

  const handleDelete = async () => {
    if (!canDelete) return;

    try {
      setSaving(true);
      setErr('');
      await onConfirm?.(paymentId);
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Ödeme silinemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: `linear-gradient(180deg, ${alpha(theme.palette.error.main, 0.1)}, ${alpha(
            theme.palette.background.paper,
            0.95
          )})`,
        }}
      >
        <MDBox
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
            background: alpha(theme.palette.error.main, 0.08),
          }}
        >
          <Icon sx={{ fontSize: 18, color: alpha(theme.palette.error.main, 0.95) }}>delete</Icon>
        </MDBox>

        <MDBox>
          <MDTypography fontWeight="bold">Ödemeyi Sil</MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            Bu işlem geri alınamaz
          </MDTypography>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1 }}>
          <MDTypography>
            <b>{memberLabel}</b> için alınan aşağıdaki ödemeyi silmek istediğine emin misin?
          </MDTypography>

          <MDBox
            sx={{
              mt: 0.5,
              p: 1.25,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: alpha(theme.palette.background.paper, 0.6),
              display: 'grid',
              gap: 0.5,
            }}
          >
            <MDTypography fontWeight="bold">{money(amountTry)}</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              {paidDate} • {method} • #{paymentId ?? '—'}
            </MDTypography>
            {payment?.note ? (
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Not: {payment.note}
              </MDTypography>
            ) : null}
          </MDBox>

          {err ? (
            <MDBox
              sx={{
                mt: 0.75,
                p: 1,
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                background: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography variant="caption" sx={{ color: 'error.main' }}>
                {err}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={saving}
          sx={{ borderRadius: '12px' }}
        >
          Vazgeç
        </MDButton>
        <MDBox sx={{ flex: 1 }} />
        <MDButton
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!canDelete}
          sx={{ borderRadius: '12px' }}
        >
          {saving ? 'Siliniyor...' : 'Evet, Sil'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
